import { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Product, ProductPricing } from '@mews-commander/data-access';
import { FormCheckboxField, FormCounterField, LocalizedProperty, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Stack } from '@optimus-web/core';
import { DateTimeField, SelectField } from '@mews-commander/form';
import { ProductSummary } from './ProductSummary';
import { AddProductsFormValueKey } from './types';
import { AddProductsToReservationPanelDataTestIds } from './constants';

export interface AddProductsFormValues {
    [AddProductsFormValueKey.productId]: string | null;
    [AddProductsFormValueKey.consumedUtc]: string | null;
    [AddProductsFormValueKey.quantity]: number;
    [AddProductsFormValueKey.claimForToday]: boolean;
}

interface AddProductsFormProps {
    products: Product[];
}

export const AddProductsForm: FC<AddProductsFormProps> = ({ products }) => {
    const { watch } = useFormContext<AddProductsFormValues>();
    const values = watch();

    const selectedProduct = values.productId ? products.find(({ id }) => id === values.productId) : undefined;

    const productOptions = useMemo(
        () =>
            products.map(({ names, id }) => ({
                value: id,
                label: <LocalizedProperty property={names} />,
                disabled: false,
                inactive: false,
                indent: false,
                secondaryText: null,
            })),
        [products]
    );

    return (
        <Stack vertical distribution="space-between" fill>
            <Stack vertical spacing="200">
                <SelectField
                    required
                    placeholder={<LocalizedText textKey={TextKey.SelectProduct} />}
                    label={<LocalizedText textKey={TextKey.Product} />}
                    name={AddProductsFormValueKey.productId}
                    options={productOptions}
                />

                <DateTimeField
                    name={AddProductsFormValueKey.consumedUtc}
                    label={<LocalizedText textKey={TextKey.ConsumptionDate} />}
                    required
                    dateProps={{ disabled: !values[AddProductsFormValueKey.productId] }}
                />

                <FormCounterField
                    label={<LocalizedText textKey={TextKey.Quantity} />}
                    name={AddProductsFormValueKey.quantity}
                    min={1}
                />

                <FormCheckboxField
                    name={AddProductsFormValueKey.claimForToday}
                    data-test-id={AddProductsToReservationPanelDataTestIds.claimProductsCheckbox}
                >
                    <LocalizedText textKey={TextKey.ClaimAddedProductsForToday} />
                </FormCheckboxField>
            </Stack>

            {/* // For now only products charged once with absolute pricing are displayed */}
            {selectedProduct?.pricing.discriminator === ProductPricing.Absolute && !!values.quantity && (
                <ProductSummary
                    chargingMode={selectedProduct.chargingMode}
                    price={selectedProduct.pricing.value}
                    quantity={values.quantity}
                />
            )}
        </Stack>
    );
};
